import { Component, ErrorHandler, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, metadata, required, REQUIRED, SchemaPath, validate } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { CustomerBeneficiary, CustomerHallituksenJasen, CustomerOsakas, KnowYourCustomer } from '@lemon/core-firebase/model/kayttaja'
import { KnowYourCustomerSaveRequest } from '@lemon/core-firebase/model/sopimus'
import { ErrorResponse } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { LemonaidValidators } from 'app/_angular/_validator/LemonaidValidators'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { TuntemistiedotDialogService } from 'app/_angular/service/tuntemistiedot-dialog.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from 'app/_jaettu-angular/service/maa.service'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil, takeWhile } from 'rxjs/operators'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { PepTekstiSharedDialog } from './pep-teksti-shared.dialog'

export interface UudetTuntemistiedotDialogData {
  kyc?: KnowYourCustomer
}

interface HenkiloLomake {
  nimi: string
  hetu: string
  kansallisuus: string | null
  pep: boolean | null
  noFinnishId: boolean | null
  birthDate: Date | null
}

interface OsakasLomake extends HenkiloLomake {
  osakkeita: number | null
  onkoYritys: boolean | null
}

interface HallituksenJasenLomake extends HenkiloLomake {
  rooli: CustomerHallituksenJasen['rooli'] | null
}

interface TuntemistiedotLomake {
  osakkaat: OsakasLomake[]
  hallitus: HallituksenJasenLomake[]
  muut: HenkiloLomake[]
}

// Reaktiivisen lomakkeen required hyväksyy arvon false, Signal Formsin required ei
function vaadiTotuusarvo(polku: SchemaPath<boolean | null>) {
  metadata(polku, REQUIRED, () => true)
  validoiReaktiivisesti(polku, Validators.required)
}

function vaadiVahintaanYksi(polku: SchemaPath<unknown[]>) {
  validate(polku, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
}

@Component({
  templateUrl: './uudet-tuntemistiedot.dialog.html',
  styleUrls: ['./uudet-tuntemistiedot.dialog.css'],
  // Valintanappiryhmän virhetyyli (styles.css) nojaa reaktiivisten lomakkeiden ng-invalid- ja ng-touched-luokkiin
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UudetTuntemistiedotDialog implements OnInit, OnDestroy {
  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  lomake = form(signal<TuntemistiedotLomake>({ osakkaat: [], hallitus: [], muut: [] }), polku => {
    vaadiVahintaanYksi(polku.osakkaat)
    applyEach(polku.osakkaat, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishHetuYtunnusOrVatNumberValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      validoiReaktiivisesti(rivi.hetu, kontrolli => this.isCompanysOwnYtunnusValidator(kontrolli))
      required(rivi.osakkeita)
      required(rivi.kansallisuus)
      vaadiTotuusarvo(rivi.pep)
      vaadiTotuusarvo(rivi.onkoYritys)
      disabled(rivi.onkoYritys, ({ valueOf }) => !valueOf(rivi.noFinnishId))
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId) || !!valueOf(rivi.onkoYritys))
    })
    vaadiVahintaanYksi(polku.hallitus)
    applyEach(polku.hallitus, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishPersonalIdValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.kansallisuus)
      vaadiTotuusarvo(rivi.pep)
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId))
      required(rivi.rooli)
    })
    applyEach(polku.muut, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishPersonalIdValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.kansallisuus)
      vaadiTotuusarvo(rivi.pep)
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId))
    })
  })
  kansallisuudetObservable: Observable<Maa[]>
  hallitusRoolitObservable: Observable<{ avain: Pick<CustomerHallituksenJasen, 'rooli'>; nimi: string }[]>

  readonly commonError = signal<string | null>(null)

  private _tunnistamistiedot: KnowYourCustomer = {
    asiakasAvain: null,
    avain: null,
    edunsaajat: [],
    hallitus: [],
    luoja: null,
    luotu: null,
    osakkaat: [],
    paivitetty: null,
    paivittaja: null,
    poistettu: false,
  }

  private readonly _ytunnus = signal<string | undefined>(undefined)
  private readonly _injector = inject(Injector)
  private _osakkaidenLopetukset: Subject<void>[] = []
  private _hallituksenLopetukset: Subject<void>[] = []
  private _edunsaajienLopetukset: Subject<void>[] = []

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: UudetTuntemistiedotDialogData,
    private _dialogRef: MatDialogRef<UudetTuntemistiedotDialog>,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _maaService: MaaService,
    private _dialog: MatDialog,
    private _tuntemistiedotDialogService: TuntemistiedotDialogService,
    private _dateService: DateService,
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this._dialogRef.disableClose = true
  }

  ngOnInit() {
    this._kayttajaService.nykyinenAsiakasObservable.pipe(takeWhile(asiakas => !asiakas, true)).subscribe(asiakas => {
      if (asiakas.ytunnus) {
        this._ytunnus.set(asiakas.ytunnus.trim())
      }
    })

    this._kayttajaService
      .getKayttajanTiedot()
      .then(kayttajanTiedot => {
        if (kayttajanTiedot?.asiakasAvain) {
          this._tuntemistiedotDialogService.savePopupSeenLog(kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid)
        }
      })
      .catch(err => {
        console.log(err)
      })

    this.hallitusRoolitObservable = this._translationService.currentLanguageObservable.pipe(
      takeUntil(this._ngUnsubscribe),
      map(kieli => {
        const kaytettyKieli = kieli === 'en' ? 'en' : 'fi'
        const roolit = [
          { avain: 'puheenjohtaja', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-puheenjohtaja', kaytettyKieli) },
          { avain: 'jasen', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-jasen', kaytettyKieli) },
          { avain: 'varajasen', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-varajasen', kaytettyKieli) },
          { avain: 'toimitusjohtaja', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.toimitusjohtaja', kaytettyKieli) },
        ] as { avain: Pick<CustomerHallituksenJasen, 'rooli'>; nimi: string }[]

        return roolit
      }),
    )

    this.kansallisuudetObservable = this._maaService.kaikkiMaatObservable.pipe(
      takeUntil(this._ngUnsubscribe),
      map(kaikkiMaat => {
        const maatCopy = [...kaikkiMaat]
        // Make selecting a bit easier
        const suomi = kaikkiMaat.find(maa => maa.koodi === 'FIN')
        const ruotsi = kaikkiMaat.find(maa => maa.koodi === 'SWE')
        maatCopy.unshift({ nimi: '---', koodi: null })
        maatCopy.unshift(ruotsi)
        maatCopy.unshift(suomi)
        return maatCopy
      }),
    )

    if (this._data.kyc) {
      for (const osakas of this._data.kyc.osakkaat || []) {
        this.lisaaOsakas(osakas)
      }
      for (const jasen of this._data.kyc.hallitus || []) {
        this.lisaaHallituksenJasen(jasen)
      }
      for (const edunsaaja of this._data.kyc.edunsaajat || []) {
        this.lisaaEdunsaaja(edunsaaja)
      }
    }
  }

  get osakasRivit() {
    return this.lomake.osakkaat
  }

  get hallitusRivit() {
    return this.lomake.hallitus
  }

  get edunsaajaRivit() {
    return this.lomake.muut
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private _rivinMuutokset<T>(kentta: FieldTree<T>, lopetus: Subject<void>) {
    return arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe), takeUntil(lopetus))
  }

  poistaOsakas(index: number) {
    this._osakkaidenLopetukset.splice(index, 1)[0]?.next()
    asetaArvo(
      this.osakasRivit,
      this.osakasRivit()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._tunnistamistiedot.osakkaat.splice(index, 1)
  }

  lisaaOsakas(firestoresta?: CustomerOsakas) {
    const osakas: CustomerOsakas = firestoresta
      ? firestoresta
      : {
          hetuTaiYtunnus: '',
          nimi: '',
          osakkeita: null,
          kansallisuus: null,
          pep: null,
        }

    const rivit = this.osakasRivit().value()
    asetaArvo(
      this.osakasRivit,
      [
        ...rivit,
        {
          nimi: osakas.nimi ?? '',
          hetu: osakas.hetuTaiYtunnus ?? '',
          osakkeita: osakas.osakkeita,
          kansallisuus: osakas.kansallisuus,
          pep: osakas.pep,
          noFinnishId: null,
          onkoYritys: null,
          birthDate: osakas.birthDate ? this._dateService.localDateToDate(osakas.birthDate) : null,
        },
      ],
      { ilmoita: false },
    )
    const osakasRivi = this.osakasRivit[rivit.length]
    const lopetus = new Subject<void>()
    this._osakkaidenLopetukset.push(lopetus)

    this._rivinMuutokset(osakasRivi.nimi, lopetus).subscribe(value => {
      osakas.nimi = value || null
    })
    this._rivinMuutokset(osakasRivi.hetu, lopetus).subscribe(value => {
      osakas.hetuTaiYtunnus = value || null
    })
    this._rivinMuutokset(osakasRivi.osakkeita, lopetus).subscribe(value => {
      osakas.osakkeita = value || null
    })
    this._rivinMuutokset(osakasRivi.kansallisuus, lopetus).subscribe(value => {
      osakas.kansallisuus = value || null
    })
    this._rivinMuutokset(osakasRivi.pep, lopetus).subscribe(value => {
      osakas.pep = value ?? null
    })
    this._rivinMuutokset(osakasRivi.noFinnishId, lopetus).subscribe(value => {
      osakas.noFinnishId = value ?? false
    })

    this._rivinMuutokset(osakasRivi.birthDate, lopetus).subscribe(value => {
      if (value && osakasRivi.birthDate().valid()) {
        osakas.birthDate = this._dateService.dateToLocalDate(value)
      }
    })

    this._rivinMuutokset(osakasRivi.onkoYritys, lopetus).subscribe(value => {
      osakas.onkoYritys = value ?? null
    })

    // Note! Do not change order of these setValues
    asetaArvo(osakasRivi.onkoYritys, osakas.onkoYritys ?? null)
    asetaArvo(osakasRivi.noFinnishId, osakas.noFinnishId ?? null)

    this._tunnistamistiedot.osakkaat.push(osakas)
  }

  poistaHallituksenJasen(index: number) {
    this._hallituksenLopetukset.splice(index, 1)[0]?.next()
    asetaArvo(
      this.hallitusRivit,
      this.hallitusRivit()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._tunnistamistiedot.hallitus.splice(index, 1)
  }

  lisaaHallituksenJasen(firestoresta?: CustomerHallituksenJasen) {
    const jasen: CustomerHallituksenJasen = firestoresta
      ? firestoresta
      : {
          hetuTaiYtunnus: '',
          nimi: '',
          kansallisuus: null,
          pep: null,
          rooli: null,
        }

    const rivit = this.hallitusRivit().value()
    asetaArvo(
      this.hallitusRivit,
      [
        ...rivit,
        {
          nimi: jasen.nimi ?? '',
          hetu: jasen.hetuTaiYtunnus ?? '',
          kansallisuus: jasen.kansallisuus,
          pep: jasen.pep,
          noFinnishId: null,
          birthDate: jasen.birthDate ? this._dateService.localDateToDate(jasen.birthDate) : null,
          rooli: jasen.rooli,
        },
      ],
      { ilmoita: false },
    )
    const jasenRivi = this.hallitusRivit[rivit.length]
    const lopetus = new Subject<void>()
    this._hallituksenLopetukset.push(lopetus)

    this._rivinMuutokset(jasenRivi.nimi, lopetus).subscribe(value => {
      jasen.nimi = value || null
    })
    this._rivinMuutokset(jasenRivi.hetu, lopetus).subscribe(value => {
      jasen.hetuTaiYtunnus = value || null
    })
    this._rivinMuutokset(jasenRivi.kansallisuus, lopetus).subscribe(value => {
      jasen.kansallisuus = value || null
    })
    this._rivinMuutokset(jasenRivi.pep, lopetus).subscribe(value => {
      jasen.pep = value ?? null
    })
    this._rivinMuutokset(jasenRivi.noFinnishId, lopetus).subscribe(value => {
      jasen.noFinnishId = value ?? false
    })
    this._rivinMuutokset(jasenRivi.birthDate, lopetus).subscribe(value => {
      if (value && jasenRivi.birthDate().valid()) {
        jasen.birthDate = this._dateService.dateToLocalDate(value)
      }
    })
    this._rivinMuutokset(jasenRivi.rooli, lopetus).subscribe(value => {
      jasen.rooli = value || null
    })

    asetaArvo(jasenRivi.noFinnishId, jasen.noFinnishId ?? null)
    this._tunnistamistiedot.hallitus.push(jasen)
  }

  poistaEdunsaaja(index: number) {
    this._edunsaajienLopetukset.splice(index, 1)[0]?.next()
    asetaArvo(
      this.edunsaajaRivit,
      this.edunsaajaRivit()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._tunnistamistiedot.edunsaajat.splice(index, 1)
  }

  lisaaEdunsaaja(firestoresta?: CustomerBeneficiary) {
    const saaja: CustomerBeneficiary = firestoresta
      ? firestoresta
      : {
          hetuTaiYtunnus: '',
          nimi: '',
          kansallisuus: null,
          pep: null,
        }

    const rivit = this.edunsaajaRivit().value()
    asetaArvo(
      this.edunsaajaRivit,
      [
        ...rivit,
        {
          nimi: saaja.nimi ?? '',
          hetu: saaja.hetuTaiYtunnus ?? '',
          kansallisuus: saaja.kansallisuus,
          pep: saaja.pep,
          noFinnishId: null,
          birthDate: saaja.birthDate ? this._dateService.localDateToDate(saaja.birthDate) : null,
        },
      ],
      { ilmoita: false },
    )
    const saajaRivi = this.edunsaajaRivit[rivit.length]
    const lopetus = new Subject<void>()
    this._edunsaajienLopetukset.push(lopetus)

    this._rivinMuutokset(saajaRivi.nimi, lopetus).subscribe(value => {
      saaja.nimi = value || null
    })
    this._rivinMuutokset(saajaRivi.hetu, lopetus).subscribe(value => {
      saaja.hetuTaiYtunnus = value || null
    })
    this._rivinMuutokset(saajaRivi.kansallisuus, lopetus).subscribe(value => {
      saaja.kansallisuus = value || null
    })
    this._rivinMuutokset(saajaRivi.pep, lopetus).subscribe(value => {
      saaja.pep = value ?? null
    })
    this._rivinMuutokset(saajaRivi.noFinnishId, lopetus).subscribe(value => {
      saaja.noFinnishId = value ?? false
    })
    this._rivinMuutokset(saajaRivi.birthDate, lopetus).subscribe(value => {
      if (value && saajaRivi.birthDate().valid()) {
        saaja.birthDate = this._dateService.dateToLocalDate(value)
      }
    })

    asetaArvo(saajaRivi.noFinnishId, saaja.noFinnishId ?? null)

    this._tunnistamistiedot.edunsaajat.push(saaja)
  }

  tallennaKyc() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    // Show loading indicator
    this._ladataanService.aloitaLataaminen()

    // Remove unnecessary data (birthDate & onkoYritys fields are disabled when the osakas has a Finnish hetu/y-tunnus)
    for (const osakas of this._tunnistamistiedot.osakkaat) {
      if (!osakas.noFinnishId) {
        osakas.birthDate = null
        osakas.onkoYritys = null
      }
    }

    // Call CF
    const tallennusData: KnowYourCustomerSaveRequest = {
      kyc: this._tunnistamistiedot,
    }
    return this._firebaseLemonaid
      .functionsCall<KnowYourCustomerSaveRequest, ErrorResponse>('knowYourCustomerTallennus', tallennusData)
      .then(response => {
        // Stop loading indicator
        this._ladataanService.lopetaLataaminen()

        // If no response, or response contains an error
        if (!response || response.e) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          // All good, close dialog
          this._dialogRef.close()
        }
      })
      .catch(error => {
        this._ladataanService.lopetaLataaminen()
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
        this._errorHandler.handleError(error)
      })
  }

  postpone() {
    this._tuntemistiedotDialogService.postpone()
    this._dialogRef.close()
  }

  openPepText() {
    this._dialog.open(PepTekstiSharedDialog)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  isCompanysOwnYtunnusValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!!this._ytunnus() && this._ytunnus() === control.value?.trim()) {
      return { ownytunnus: true }
    }
    return null
  }
}
